/**
 * canvasZoom — a tiny shared signal carrying the live xyflow canvas zoom out to
 * editor chrome that lives OUTSIDE the ReactFlowProvider. The floating Add
 * palette and its dock group are rendered as SIBLINGS of `SitemapCanvas` (see
 * EditorScreen), so they can't call `useViewport()`; instead the canvas
 * publishes zoom from inside the provider (`publishCanvasZoom`) and any sibling
 * reads it reactively (`useCanvasZoomValue`). Module singleton, same idiom as
 * panelDock — simpler than threading a provider across two coordinate systems.
 */
import { useSyncExternalStore } from "react";

let zoom = 1;
const listeners = new Set<() => void>();

/** Called from inside the canvas (ReactFlow provider) whenever zoom changes. */
export function publishCanvasZoom(next: number): void {
  if (next === zoom) return;
  zoom = next;
  for (const l of listeners) l();
}

/** Reactive read for chrome outside the provider. Defaults to 1 (also the
 *  native fallback, where nothing publishes). */
export function useCanvasZoomValue(): number {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => zoom,
    () => zoom,
  );
}

// LOD band thresholds (canvas zoom). Owned here as the single source of truth so
// both the page LOD (usePageNode) and zoom-gated chrome (the Add palette) agree.
//   • < WIREFRAME_MAX_ZOOM  → wireframe band: sections render as flat SVGs with
//     NO mounted nested grid, so a dragged WIDGET has no valid target.
//   • ≥ EDIT_MIN_ZOOM       → fully editable (near 1:1).
export const WIREFRAME_MAX_ZOOM = 360 / 1440; // 0.25  (360px ÷ desktop width)
export const EDIT_MIN_ZOOM = 1100 / 1440; // ~0.76  (1100px ÷ desktop width)
